// Font pairings for the May 2026 styleguide picker.
// Each pairing is a display-font + body-font pair, loaded from
// Google Fonts at runtime via a <link rel="stylesheet"> appended to
// <head>. The active selection toggles --font-display + --font-body
// in :root via the styleguide-theme-apply.tsx runtime.
//
// Naming: short descriptive id, human name, then a one-line "use
// when" hint to make the picker easier to scan.

export type FontPair = {
  id: string
  name: string
  description: string
  /** Display font CSS family stack (used for headings, hero) */
  displayFamily: string
  /** Body font CSS family stack */
  bodyFamily: string
  /** Google Fonts URL fragment to inject; null = system fonts only */
  googleFontsUrl: string | null
  /** What font family it's trying to evoke (for the picker label) */
  vibe: string
}

export const FONT_PAIRS: FontPair[] = [
  {
    id: 'space-grotesk-inter-tight',
    name: 'Space Grotesk + Inter Tight',
    description: 'The current default. Geometric display + neutral body.',
    displayFamily: `'Space Grotesk', ui-sans-serif, system-ui, sans-serif`,
    bodyFamily: `'Inter Tight', 'Inter', ui-sans-serif, system-ui, sans-serif`,
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter+Tight:wght@400;500;600;700;800&display=swap',
    vibe: 'Modern, geometric, neutral',
  },
  {
    id: 'inter-source-serif',
    name: 'Inter + Source Serif 4',
    description: 'Closest open-source analogue to Söhne + Tiempos (Anthropic stack).',
    displayFamily: `'Source Serif 4', 'Source Serif Pro', Georgia, serif`,
    bodyFamily: `'Inter', ui-sans-serif, system-ui, sans-serif`,
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600;8..60,700&display=swap',
    vibe: 'Editorial, warm, Anthropic-adjacent',
  },
  {
    id: 'geist-geist-mono',
    name: 'Geist + Geist Mono',
    description: 'Vercel\'s open-source. Geometric sans + matching mono. Devtool feel.',
    displayFamily: `'Geist', ui-sans-serif, system-ui, sans-serif`,
    bodyFamily: `'Geist', ui-sans-serif, system-ui, sans-serif`,
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap',
    vibe: 'Modern devtool, sans-only',
  },
  {
    id: 'ibm-plex',
    name: 'IBM Plex Sans + Plex Serif',
    description: 'IBM\'s family. Sans for body + serif for editorial display. Institutional.',
    displayFamily: `'IBM Plex Serif', Georgia, serif`,
    bodyFamily: `'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif`,
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Serif:wght@400;500;600;700&display=swap',
    vibe: 'Enterprise B2B, institutional',
  },
  {
    id: 'dm-sans-serif',
    name: 'DM Sans + DM Serif Display',
    description: 'DM Sans body + dramatic DM Serif Display for headlines. Editorial pop.',
    displayFamily: `'DM Serif Display', Georgia, serif`,
    bodyFamily: `'DM Sans', ui-sans-serif, system-ui, sans-serif`,
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display:wght@400&display=swap',
    vibe: 'Editorial display, modern body',
  },
  {
    id: 'manrope-lora',
    name: 'Manrope + Lora',
    description: 'Soft sans for body + readable Lora serif for display. Warm, brand-friendly.',
    displayFamily: `'Lora', Georgia, serif`,
    bodyFamily: `'Manrope', ui-sans-serif, system-ui, sans-serif`,
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&family=Manrope:wght@400;500;600;700;800&display=swap',
    vibe: 'Warm, brand-friendly',
  },
  {
    id: 'jakarta-fraunces',
    name: 'Plus Jakarta + Fraunces',
    description: 'Modern sans + characterful serif display. Distinctive, art-directed.',
    displayFamily: `'Fraunces', Georgia, serif`,
    bodyFamily: `'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif`,
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap',
    vibe: 'Distinctive, art-directed',
  },
  {
    id: 'work-sans-only',
    name: 'Work Sans (single family)',
    description: 'Single neutral sans across display + body. Quietly elegant, low-noise.',
    displayFamily: `'Work Sans', ui-sans-serif, system-ui, sans-serif`,
    bodyFamily: `'Work Sans', ui-sans-serif, system-ui, sans-serif`,
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700;800&display=swap',
    vibe: 'Quiet, single-family',
  },
]

export function getFontPair(id: string): FontPair | undefined {
  return FONT_PAIRS.find((p) => p.id === id)
}
